import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { accountSelector, setNaming } from '../services/AccountSlice';
import { nameAvailable, setLeaderboardName } from './session';
import { auth } from '../firebase';

/**
 * Choosing the name a signed-in player has on the leaderboard. Names are
 * unique (ignoring case, accents and spacing), checked as they type.
 */
const NameDialog = () => {
    const dispatch = useDispatch();
    const { naming, profile } = useSelector(accountSelector);
    const [name, setName] = useState('');
    const [check, setCheck] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Start from their current name, or their first name from Google.
    useEffect(() => {
        if (naming) {
            const google = auth.currentUser?.displayName?.split(' ')[0] || '';
            setName(profile?.name || google);
            setError(null);
            setCheck(null);
        }
    }, [naming, profile?.name]);

    useEffect(() => {
        if (!naming || name.trim().length === 0) {
            setCheck(null);
            return undefined;
        }
        let alive = true;
        setCheck({ checking: true });
        const timer = setTimeout(() => {
            nameAvailable(name).then(answer => alive && setCheck(answer)).catch(() => alive && setCheck(null));
        }, 350);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [name, naming]);

    if (!profile) {
        return null;
    }
    const close = () => dispatch(setNaming(false));
    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await setLeaderboardName(dispatch, name);
            close();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };
    const unchanged = profile.name && name.trim() === profile.name;
    const helper = error || (check?.checking ? 'Checking…' : check?.problem) || (check?.available && !unchanged ? 'Available' : ' ');

    return (
        <Dialog open={naming} onClose={close} fullWidth maxWidth="xs">
            <DialogTitle>{profile.name ? 'Change your leaderboard name' : 'Choose your leaderboard name'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This is the name everyone sees on the leaderboard. In games you still use whatever name you type in.
                    {!profile.name && ` Until you choose one you're ${profile.displayName}.`}
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Leaderboard name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter' && check?.available && !saving) save(); }}
                    inputProps={{ maxLength: 20, 'aria-describedby': 'name-help' }}
                    error={Boolean(error) || (check && !check.checking && check.available === false)}
                    helperText={<span id="name-help" className={check?.available && !error ? 'name-ok' : undefined}>{helper}</span>}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={close}>{profile.name ? 'Cancel' : 'Not now'}</Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={saving || unchanged || !check?.available}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                >Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NameDialog;
